import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  posLogin,
  posLogout,
  validatePosSession,
  getPosInventory,
  getPosSales,
  createPosSale,
  getOnlineOrdersCount
} from '../../services/api';
import { 
  Search, 
  Plus, 
  Minus, 
  Trash2, 
  CreditCard, 
  Banknote, 
  Receipt, 
  Package,
  Settings,
  RefreshCw,
  Wifi,
  WifiOff,
  X,
  Check,
  ShoppingCart,
  Printer,
  History,
  Calculator,
  LogOut,
  Key,
  Store,
  User,
  ArrowRight,
  Building2,
  ShoppingBag,
  Bell,
  CloudOff
} from 'lucide-react';
import { toast } from 'sonner';
import { getErrorMessage } from '../../utils/errorUtils';
import QRCode from 'qrcode';
import OnlineOrdersView from './OnlineOrdersView';
import OfflineIndicator from '../../components/pos/OfflineIndicator';
import offlineDB from '../../utils/offlineDB';
import syncManager from '../../utils/syncManager';

// Generate unique device ID
const getDeviceId = () => {
  let deviceId = localStorage.getItem('pos_device_id');
  if (!deviceId) {
    deviceId = 'DEV-' + Math.random().toString(36).substr(2, 9) + '-' + Date.now();
    localStorage.setItem('pos_device_id', deviceId);
  }
  return deviceId;
};

// ==================== POS LOGIN SCREEN ====================
const POSLoginScreen = ({ onLogin }) => {
  const savedHash = localStorage.getItem('pos_terminal_hash') || '';
  const [step, setStep] = useState(savedHash ? 'pin' : 'hash');
  const [terminalHash, setTerminalHash] = useState(savedHash);
  const [pin, setPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const pinInputRefs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  const handleHashSubmit = (e) => {
    e.preventDefault();
    if (terminalHash.trim().length >= 8) {
      setStep('pin');
      setError('');
      setTimeout(() => pinInputRefs[0].current?.focus(), 100);
    } else {
      setError('El código de instalación debe tener al menos 8 caracteres');
    }
  };

  const handlePinChange = (index, value) => {
    if (!/^\d*$/.test(value)) return;
    
    const newPin = pin.split('');
    newPin[index] = value;
    setPin(newPin.join(''));
    
    if (value && index < 5) {
      pinInputRefs[index + 1].current?.focus();
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0) {
      pinInputRefs[index - 1].current?.focus();
    }
  };

  const handlePinSubmit = async () => {
    if (pin.length !== 6) {
      setError('Ingresa los 6 dígitos del PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await posLogin(terminalHash.trim(), pin, getDeviceId());
      
      // Store session data
      localStorage.setItem('pos_session', JSON.stringify(response.data));
      onLogin(response.data);
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.detail || 'Error al iniciar sesión');
      setPin('');
      pinInputRefs[0].current?.focus();
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (pin.length === 6) {
      handlePinSubmit();
    }
  }, [pin]);

  // Auto-focus first PIN input when starting on PIN step (saved terminal)
  useEffect(() => {
    if (step === 'pin') {
      setTimeout(() => pinInputRefs[0].current?.focus(), 100);
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-2xl shadow-teal-500/25">
            <Store className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white">Todo en Local</h1>
          <p className="text-slate-400 mt-1">Punto de Venta</p>
        </div>

        {/* Form Card */}
        <div className="bg-slate-900 rounded-2xl border border-slate-800 p-6 shadow-xl">
          {step === 'hash' ? (
            <form onSubmit={handleHashSubmit}>
              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-400 mb-2">
                  Código de Instalación
                </label>
                <div className="relative">
                  <Key className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={terminalHash}
                    onChange={(e) => setTerminalHash(e.target.value)}
                    placeholder="Ej: a1b2c3d4e5f6..."
                    className="w-full pl-12 pr-4 py-4 bg-slate-800 border border-slate-700 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all font-mono tracking-wider"
                    autoFocus
                    data-testid="pos-hash-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-2">
                  Ingresa el código que recibiste al configurar la terminal
                </p>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}

              <button
                type="submit"
                className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 transition-all flex items-center justify-center gap-2"
                data-testid="pos-hash-submit"
              >
                Continuar
                <ArrowRight className="w-5 h-5" />
              </button>
            </form>
          ) : (
            <div>
              <button
                onClick={() => { setStep('hash'); setPin(''); setError(''); setTerminalHash(''); localStorage.removeItem('pos_terminal_hash'); }}
                className="text-slate-400 hover:text-white mb-4 flex items-center gap-1 text-sm"
              >
                ← Cambiar terminal
              </button>

              <div className="mb-6">
                <label className="block text-sm font-medium text-slate-400 mb-4 text-center">
                  Ingresa tu PIN de cajero
                </label>
                <div className="flex justify-center gap-2">
                  {[0, 1, 2, 3, 4, 5].map((index) => (
                    <input
                      key={index}
                      ref={pinInputRefs[index]}
                      type="password"
                      inputMode="numeric"
                      maxLength={1}
                      value={pin[index] || ''}
                      onChange={(e) => handlePinChange(index, e.target.value)}
                      onKeyDown={(e) => handlePinKeyDown(index, e)}
                      className="w-12 h-14 text-center text-2xl font-bold bg-slate-800 border border-slate-700 rounded-xl text-white focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                      data-testid={`pos-pin-${index}`}
                    />
                  ))}
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-900/30 border border-red-500/50 rounded-lg text-red-400 text-sm text-center">
                  {error}
                </div>
              )}

              {loading && (
                <div className="flex items-center justify-center py-4">
                  <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
            </div>
          )}
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          ¿Problemas para acceder? Contacta al administrador
        </p>
      </div>
    </div>
  );
};

// ==================== MIXED PAYMENT MODAL ====================
const MixedPaymentModal = ({ total, onPayment, onClose }) => {
  const [payments, setPayments] = useState([
    { id: 1, method: 'cash', amount: total, cashReceived: 0 }
  ]);

  const paymentMethods = [
    { value: 'cash', label: 'Efectivo', icon: Banknote, color: 'emerald' },
    { value: 'debit', label: 'Débito', icon: CreditCard, color: 'blue' },
    { value: 'credit', label: 'Crédito', icon: CreditCard, color: 'purple' },
    { value: 'transfer', label: 'Transferencia', icon: Receipt, color: 'orange' }
  ];

  const totalEntered = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const remaining = total - totalEntered;
  const canConfirm = totalEntered >= total;
  const change = payments.find(p => p.method === 'cash')?.cashReceived 
    ? Math.max(0, payments.find(p => p.method === 'cash').cashReceived - payments.find(p => p.method === 'cash').amount)
    : 0;

  const addPayment = () => {
    const newId = Math.max(...payments.map(p => p.id)) + 1;
    setPayments([...payments, { 
      id: newId, 
      method: 'debit', 
      amount: remaining > 0 ? remaining : 0,
      cashReceived: 0
    }]);
  };

  const removePayment = (id) => {
    if (payments.length > 1) {
      setPayments(payments.filter(p => p.id !== id));
    }
  };

  const updatePayment = (id, field, value) => {
    setPayments(payments.map(p => 
      p.id === id ? { ...p, [field]: value } : p
    ));
  };

  const autoFillAmount = (id) => {
    const otherTotal = payments.filter(p => p.id !== id).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    const autoAmount = Math.max(0, total - otherTotal);
    updatePayment(id, 'amount', autoAmount);
  };

  const handleConfirm = () => {
    if (!canConfirm) return;
    
    // Format payments for API
    const formattedPayments = payments.map(p => ({
      method: p.method,
      amount: parseFloat(p.amount) || 0
    }));

    onPayment(formattedPayments, change);
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-900 rounded-2xl max-w-lg w-full p-6 border border-slate-800 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Finalizar Venta</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Total to charge */}
        <div className="bg-slate-800 rounded-xl p-4 mb-6">
          <p className="text-sm text-slate-400">Total a cobrar</p>
          <p className="text-3xl font-bold text-teal-400">${total.toFixed(2)}</p>
        </div>

        {/* Payment methods */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-medium text-white">Métodos de Pago</h3>
            <button
              onClick={addPayment}
              className="text-sm text-teal-400 hover:text-teal-300 flex items-center gap-1"
            >
              <Plus className="w-4 h-4" />
              Agregar método
            </button>
          </div>

          <div className="space-y-4">
            {payments.map((payment, index) => (
              <div key={payment.id} className="bg-slate-800 rounded-xl p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm text-slate-400">Pago {index + 1}</span>
                  {payments.length > 1 && (
                    <button
                      onClick={() => removePayment(payment.id)}
                      className="text-red-400 hover:text-red-300"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                {/* Method selector */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                  {paymentMethods.map(method => (
                    <button
                      key={method.value}
                      onClick={() => updatePayment(payment.id, 'method', method.value)}
                      className={`flex-1 py-2 px-3 rounded-lg text-xs font-medium transition-all flex flex-col items-center gap-1 ${
                        payment.method === method.value
                          ? `bg-${method.color}-600 text-white`
                          : 'bg-slate-700 text-slate-400 hover:bg-slate-600'
                      }`}
                      style={{
                        backgroundColor: payment.method === method.value 
                          ? (method.color === 'emerald' ? '#059669' : method.color === 'blue' ? '#2563eb' : method.color === 'purple' ? '#9333ea' : '#ea580c')
                          : undefined
                      }}
                    >
                      <method.icon className="w-4 h-4" />
                      {method.label}
                    </button>
                  ))}
                </div>

                {/* Amount */}
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="block text-xs text-slate-400 mb-1">Monto</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        value={payment.amount}
                        onChange={(e) => updatePayment(payment.id, 'amount', e.target.value)}
                        className="w-full pl-8 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-teal-500"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => autoFillAmount(payment.id)}
                      className="px-3 py-2 bg-slate-700 hover:bg-slate-600 text-teal-400 rounded-lg text-sm font-medium"
                    >
                      Auto
                    </button>
                  </div>
                </div>

                {/* Cash received (only for cash) */}
                {payment.method === 'cash' && (
                  <div>
                    <label className="block text-xs text-slate-400 mb-1">Efectivo recibido</label>
                    <div className="relative">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500">$</span>
                      <input
                        type="number"
                        value={payment.cashReceived}
                        onChange={(e) => updatePayment(payment.id, 'cashReceived', parseFloat(e.target.value) || 0)}
                        className="w-full pl-8 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-teal-500"
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Summary */}
        <div className="bg-slate-800 rounded-xl p-4 mb-6 space-y-2">
          <div className="flex justify-between text-slate-400">
            <span>Total a pagar:</span>
            <span>${total.toFixed(2)}</span>
          </div>
          <div className={`flex justify-between font-bold ${totalEntered >= total ? 'text-emerald-400' : 'text-red-400'}`}>
            <span>Total ingresado:</span>
            <span>${totalEntered.toFixed(2)}</span>
          </div>
          {remaining > 0 && (
            <div className="flex justify-between text-orange-400">
              <span>Faltante:</span>
              <span>${remaining.toFixed(2)}</span>
            </div>
          )}
          {change > 0 && (
            <div className="flex justify-between text-teal-400 text-lg">
              <span>Cambio:</span>
              <span>${change.toFixed(2)}</span>
            </div>
          )}
        </div>

        {/* Confirm button */}
        <button
          onClick={handleConfirm}
          disabled={!canConfirm}
          className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
          data-testid="confirm-sale-btn"
        >
          <Check className="w-5 h-5" />
          Confirmar Venta
        </button>
      </div>
    </div>
  );
};

// Helper to get WebSocket URL
const getWebSocketUrl = () => {
  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
  const host = window.location.host;
  return `${protocol}//${host}/api/v1`;
};

// ==================== POS MAIN COMPONENT ====================
const POSMain = () => {
  const searchInputRef = useRef(null);
  const barcodeBufferRef = useRef('');
  const barcodeTimeoutRef = useRef(null);
  const wsRef = useRef(null);
  const notificationSoundRef = useRef(null);
  const activeViewRef = useRef('sale');

  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  // POS state
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [lastSync, setLastSync] = useState(null);
  const [activeView, setActiveView] = useState('sale');
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showTicketModal, setShowTicketModal] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [ticketQR, setTicketQR] = useState('');
  const [salesHistory, setSalesHistory] = useState([]);
  const [cashCloseData, setCashCloseData] = useState(null);
  const [showMobileCart, setShowMobileCart] = useState(false);

  // Online orders state
  const [onlineOrdersCount, setOnlineOrdersCount] = useState(0);
  const [isPlayingAlert, setIsPlayingAlert] = useState(false);

  // Keep activeViewRef in sync
  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  // Check for existing session on mount
  useEffect(() => {
    checkSession();
    
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    const handleKeyDown = handleBarcodeInput;
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('keydown', handleKeyDown);
      // Close WebSocket on unmount
      if (wsRef.current) {
        wsRef.current.close();
      }
      // Stop notification sound
      if (notificationSoundRef.current) {
        clearInterval(notificationSoundRef.current);
      }
    };
  }, []);

  // Load data and connect WebSocket when session is set
  useEffect(() => {
    if (session) {
      loadProducts();
      loadSalesHistory();
      loadOnlineOrdersCount();
      connectWebSocket();
    }
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [session]);

  // Handle notification sound when orders count changes
  useEffect(() => {
    if (onlineOrdersCount > 0 && activeView !== 'orders') {
      if (!isPlayingAlert) {
        startNotificationSound();
      }
    } else {
      stopNotificationSound();
    }
  }, [onlineOrdersCount, activeView]);

  const connectWebSocket = () => {
    if (!session?.branch?.id) return;
    
    try {
      const wsUrl = `${getWebSocketUrl()}/orders/ws/${session.branch.id}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.type === 'new_order') {
            // Only update count here if NOT in orders view
            // When in orders view, OnlineOrdersView handles the update via onNewOrderCount
            if (activeViewRef.current !== 'orders') {
              setOnlineOrdersCount(prev => prev + 1);
            }
            toast.success(`¡Nuevo pedido! ${data.order.order_number}`, {
              duration: 10000,
              icon: '🛒'
            });
          }
        } catch (e) {
          console.log('WebSocket message:', event.data);
        }
      };
      
      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected, reconnecting in 5s...');
        setTimeout(connectWebSocket, 5000);
      };
      
      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
      
      // Keep alive with ping every 30s
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send('ping');
        }
      }, 30000);
      
      return () => clearInterval(pingInterval);
    } catch (error) {
      console.error('Failed to connect WebSocket:', error);
    }
  };

  const loadOnlineOrdersCount = async () => {
    if (!session?.branch?.id) return;
    try {
      const response = await getOnlineOrdersCount(session.branch.id, session);
      setOnlineOrdersCount(response.data.count);
    } catch (error) {
      console.error('Error loading orders count:', error);
    }
  };

  const startNotificationSound = () => {
    setIsPlayingAlert(true);
    playNotificationChime();
    
    // Repeat every 8 seconds
    notificationSoundRef.current = setInterval(() => {
      playNotificationChime();
    }, 8000);
  };

  const stopNotificationSound = () => {
    setIsPlayingAlert(false);
    if (notificationSoundRef.current) {
      clearInterval(notificationSoundRef.current);
      notificationSoundRef.current = null;
    }
  };

  const playNotificationChime = () => {
    try {
      const audio = new Audio('/notification-order.mp3');
      audio.volume = 0.7;
      audio.play().catch(err => {
        console.log('Could not play notification sound:', err.message);
      });
    } catch (e) {
      console.log('Audio not supported');
    }
  };

  const handleOrdersViewClick = () => {
    setActiveView('orders');
    stopNotificationSound();
  };

  const checkSession = async () => {
    try {
      // Check if there's a pending session from the dashboard modal
      const pendingSession = localStorage.getItem('pos_session_pending');
      if (pendingSession) {
        const sessionData = JSON.parse(pendingSession);
        localStorage.removeItem('pos_session_pending');
        localStorage.setItem('pos_session', JSON.stringify(sessionData));
        if (sessionData.terminal?.hash) {
          localStorage.setItem('pos_terminal_hash', sessionData.terminal.hash);
        }
        setSession(sessionData);
        return;
      }

      const savedSession = localStorage.getItem('pos_session');
      if (savedSession) {
        const sessionData = JSON.parse(savedSession);
        // Validate session is still active
        try {
          await validatePosSession(
            sessionData.session_id,
            sessionData.terminal.id,
            getDeviceId()
          );
          setSession(sessionData);
        } catch {
          // Session invalid, clear it but keep terminal hash for quick re-login
          localStorage.removeItem('pos_session');
          if (sessionData.terminal?.hash) {
            localStorage.setItem('pos_terminal_hash', sessionData.terminal.hash);
          }
        }
      }
    } catch (error) {
      console.error('Error checking session:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (sessionData) => {
    setSession(sessionData);
    setLastSync(new Date());
    // Persist terminal hash for quick re-login
    if (sessionData.terminal?.hash) {
      localStorage.setItem('pos_terminal_hash', sessionData.terminal.hash);
    }
  };

  const handleLogout = async () => {
    if (session) {
      try {
        await posLogout(session.session_id, session.terminal.id);
      } catch (error) {
        console.error('Logout error:', error);
      }
      // Keep terminal hash so next login only asks for PIN
      localStorage.removeItem('pos_session');
      setSession(null);
      setCart([]);
      setProducts([]);
    }
  };

  const loadProducts = async () => {
    if (!session?.branch?.id || !session?.session_id) return;
    try {
      const response = await getPosInventory(
        session.session_id,
        session.terminal.id,
        getDeviceId(),
        session.branch.id
      );
      const fetchedProducts = response.data.filter(p => p.quantity > 0);
      setProducts(fetchedProducts);
      setLastSync(new Date());
      
      // Cache products for offline use
      try {
        await offlineDB.cacheProducts(fetchedProducts, session.branch.id);
      } catch (cacheError) {
        console.log('Could not cache products:', cacheError);
      }
    } catch (error) {
      console.error('Error loading products:', error);
      
      // If offline, try to load from cache
      if (!navigator.onLine) {
        try {
          const cachedProducts = await offlineDB.getCachedProducts(session.branch.id);
          if (cachedProducts.length > 0) {
            setProducts(cachedProducts);
            toast.info('Productos cargados desde caché (modo offline)');
            return;
          }
        } catch (cacheError) {
          console.log('Could not load cached products:', cacheError);
        }
      }
      
      toast.error('Error al cargar productos');
    }
  };

  const loadSalesHistory = async () => {
    if (!session?.branch?.id || !session?.session_id) return;
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await getPosSales(
        session.session_id,
        session.terminal.id,
        getDeviceId(),
        session.branch.id,
        today + 'T00:00:00',
        today + 'T23:59:59'
      );
      setSalesHistory(response.data);
    } catch (error) {
      console.error('Error loading sales history:', error);
    }
  };

  // Barcode scanner handler
  const handleBarcodeInput = useCallback((e) => {
    if (document.activeElement === searchInputRef.current) return;
    
    if (e.key.length === 1) {
      barcodeBufferRef.current += e.key;
      
      if (barcodeTimeoutRef.current) {
        clearTimeout(barcodeTimeoutRef.current);
      }
      
      barcodeTimeoutRef.current = setTimeout(() => {
        if (barcodeBufferRef.current.length >= 4) {
          searchProductByBarcode(barcodeBufferRef.current);
        }
        barcodeBufferRef.current = '';
      }, 100);
    } else if (e.key === 'Enter' && barcodeBufferRef.current.length > 0) {
      searchProductByBarcode(barcodeBufferRef.current);
      barcodeBufferRef.current = '';
    }
  }, [products]);

  const searchProductByBarcode = (barcode) => {
    const product = products.find(p => 
      p.barcode === barcode || p.product_sku === barcode
    );
    if (product) {
      addToCart(product);
      toast.success(`${product.product_name} agregado`);
    } else {
      toast.error(`Producto no encontrado: ${barcode}`);
    }
  };

  // Cart operations
  const addToCart = (product) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.product_id);
      if (existing) {
        if (existing.quantity >= product.quantity) {
          toast.error('Stock insuficiente');
          return prev;
        }
        return prev.map(item =>
          item.product_id === product.product_id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, {
        product_id: product.product_id,
        product_name: product.product_name,
        product_sku: product.product_sku,
        unit_price: product.retail_price || 0,
        quantity: 1,
        max_quantity: product.quantity,
        sat_code: product.sat_code || '01010101',
        sat_unit: product.sat_unit || 'H87',
        sat_unit_name: product.sat_unit_name || 'Pieza',
        tax_config: product.tax_config || {}
      }];
    });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty < 1) return item;
        if (newQty > item.max_quantity) {
          toast.error('Stock insuficiente');
          return item;
        }
        return { ...item, quantity: newQty };
      }
      return item;
    }));
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
  };

  const clearCart = () => setCart([]);

  // Calculate totals
  const calculateTotals = () => {
    const subtotal = cart.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0);
    const taxRate = 0.16;
    const tax = subtotal * taxRate;
    const total = subtotal + tax;
    return { subtotal, tax, total };
  };

  // Process payment
  const processPayment = async (payments, change) => {
    if (cart.length === 0) {
      toast.error('El carrito está vacío');
      return;
    }

    const { subtotal, tax, total } = calculateTotals();
    
    // Use the primary payment method for the sale record
    const primaryPayment = payments[0];
    
    const saleData = {
      branch_id: session.branch.id,
      terminal_id: session.terminal.id,
      cashier_id: session.cashier.id,
      cashier_name: session.cashier.name,
      items: cart.map(item => ({
        product_id: item.product_id,
        product_name: item.product_name,
        product_sku: item.product_sku,
        quantity: item.quantity,
        unit_price: item.unit_price,
        subtotal: item.unit_price * item.quantity,
        sat_code: item.sat_code,
        sat_unit: item.sat_unit,
        sat_unit_name: item.sat_unit_name,
        taxes: {
          iva_rate: item.tax_config?.iva_rate || 0.16,
          tax_object: item.tax_config?.tax_object || '02',
          ieps_rate: item.tax_config?.ieps_rate || 0
        }
      })),
      payment_method: primaryPayment.method,
      payments: payments, // Store all payment methods
      subtotal: subtotal,
      discount: 0,
      tax: tax,
      total: total,
      change: change
    };

    // Check if online
    if (!navigator.onLine) {
      // Save sale offline
      try {
        const offlineSale = await offlineDB.saveOfflineSale(saleData);
        
        // Update cached product quantities
        for (const item of cart) {
          await offlineDB.updateCachedProductQuantity(item.product_id, item.quantity);
        }
        
        // Generate offline ticket
        const offlineTicket = {
          id: offlineSale.localId,
          ticket_number: `OFFLINE-${Date.now().toString(36).toUpperCase()}`,
          items: saleData.items,
          subtotal: saleData.subtotal,
          tax: saleData.tax,
          total: saleData.total,
          payments: payments,
          change: change,
          created_at: new Date().toISOString(),
          is_offline: true
        };
        
        setLastSale(offlineTicket);
        
        // Generate QR (will show offline notice)
        const offlineQrData = `offline:${offlineSale.localId}`;
        const qrDataUrl = await QRCode.toDataURL(offlineQrData, { width: 150 });
        setTicketQR(qrDataUrl);
        
        setShowPaymentModal(false);
        setShowTicketModal(true);
        clearCart();
        
        // Update products from cache
        const cachedProducts = await offlineDB.getCachedProducts(session.branch.id);
        setProducts(cachedProducts.filter(p => p.quantity > 0));
        
        toast.success('Venta guardada offline - Se sincronizará automáticamente', {
          icon: <CloudOff className="w-4 h-4" />,
          duration: 5000
        });
        
        // Register background sync
        syncManager.registerBackgroundSync();
        
        return;
      } catch (offlineError) {
        console.error('Error saving offline sale:', offlineError);
        toast.error('Error al guardar venta offline');
        return;
      }
    }

    // Online sale flow
    try {
      const response = await createPosSale(
        session.session_id,
        session.terminal.id,
        getDeviceId(),
        saleData
      );
      setLastSale({ ...response.data, payments, change });
      
      const invoiceUrl = `${window.location.origin}/facturacion/${response.data.id}`;
      const qrDataUrl = await QRCode.toDataURL(invoiceUrl, { width: 150 });
      setTicketQR(qrDataUrl);
      
      setShowPaymentModal(false);
      setShowTicketModal(true);
      clearCart();
      loadProducts();
      loadSalesHistory();
      
      toast.success('Venta registrada exitosamente');
    } catch (error) {
      console.error('Error processing sale:', error);
      
      // If network error, save offline
      if (!error.response || error.code === 'ERR_NETWORK') {
        try {
          const offlineSale = await offlineDB.saveOfflineSale(saleData);
          
          const offlineTicket = {
            id: offlineSale.localId,
            ticket_number: `OFFLINE-${Date.now().toString(36).toUpperCase()}`,
            items: saleData.items,
            subtotal: saleData.subtotal,
            tax: saleData.tax,
            total: saleData.total,
            payments: payments,
            change: change,
            created_at: new Date().toISOString(),
            is_offline: true
          };
          
          setLastSale(offlineTicket);
          const offlineQrData = `offline:${offlineSale.localId}`;
          const qrDataUrl = await QRCode.toDataURL(offlineQrData, { width: 150 });
          setTicketQR(qrDataUrl);
          
          setShowPaymentModal(false);
          setShowTicketModal(true);
          clearCart();
          
          toast.warning('Sin conexión - Venta guardada offline', {
            icon: <CloudOff className="w-4 h-4" />,
            duration: 5000
          });
          return;
        } catch (offlineError) {
          console.error('Error saving offline sale:', offlineError);
        }
      }
      
      const errorMessage = getErrorMessage(error, 'Error al procesar venta');
      toast.error(errorMessage);
    }
  };

  // Print ticket
  const printTicket = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generateTicketHTML());
    printWindow.document.close();
    printWindow.print();
  };

  const generateTicketHTML = () => {
    if (!lastSale || !session?.business) return '';
    
    const items = lastSale.items || [];
    const itemsHTML = items.map(item => `
      <tr>
        <td style="text-align:left">${item.product_name}</td>
        <td style="text-align:center">${item.quantity}</td>
        <td style="text-align:right">$${item.unit_price.toFixed(2)}</td>
        <td style="text-align:right">$${item.subtotal.toFixed(2)}</td>
      </tr>
    `).join('');

    const paymentsHTML = (lastSale.payments || []).map(p => {
      const methodNames = { cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', transfer: 'Transferencia' };
      return `<div class="row"><span>${methodNames[p.method] || p.method}:</span><span>$${p.amount.toFixed(2)}</span></div>`;
    }).join('');

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Ticket ${lastSale.ticket_number}</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 10px; }
          .header { text-align: center; border-bottom: 1px dashed #000; padding-bottom: 10px; margin-bottom: 10px; }
          .header h2 { margin: 0; font-size: 16px; }
          .header p { margin: 2px 0; font-size: 11px; }
          table { width: 100%; border-collapse: collapse; }
          th, td { padding: 3px 0; font-size: 11px; }
          th { border-bottom: 1px solid #000; }
          .totals { border-top: 1px dashed #000; margin-top: 10px; padding-top: 10px; }
          .row { display: flex; justify-content: space-between; margin: 3px 0; }
          .total-final { font-size: 16px; font-weight: bold; border-top: 1px solid #000; padding-top: 5px; margin-top: 5px; }
          .qr { text-align: center; margin-top: 15px; }
          .qr img { width: 120px; height: 120px; }
          .footer { text-align: center; margin-top: 15px; font-size: 10px; border-top: 1px dashed #000; padding-top: 10px; }
        </style>
      </head>
      <body>
        <div class="header">
          <h2>${session.business.name}</h2>
          <p>${session.business.address || ''}</p>
          <p>RFC: ${session.business.rfc || 'N/A'}</p>
          <p>Tel: ${session.business.phone || ''}</p>
        </div>
        
        <div style="margin-bottom:10px">
          <div><strong>Ticket:</strong> ${lastSale.ticket_number}</div>
          <div><strong>Fecha:</strong> ${new Date(lastSale.created_at).toLocaleString('es-MX')}</div>
          <div><strong>Sucursal:</strong> ${session.branch.name}</div>
          <div><strong>Cajero:</strong> ${session.cashier.name}</div>
        </div>
        
        <table>
          <thead>
            <tr>
              <th style="text-align:left">Producto</th>
              <th style="text-align:center">Cant</th>
              <th style="text-align:right">P.Unit</th>
              <th style="text-align:right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHTML}
          </tbody>
        </table>
        
        <div class="totals">
          <div class="row"><span>Subtotal:</span><span>$${lastSale.subtotal.toFixed(2)}</span></div>
          <div class="row"><span>IVA (16%):</span><span>$${lastSale.tax.toFixed(2)}</span></div>
          <div class="row total-final"><span>TOTAL:</span><span>$${lastSale.total.toFixed(2)}</span></div>
        </div>
        
        <div style="margin-top:10px; border-top: 1px dashed #000; padding-top: 10px;">
          <strong>Pagos:</strong>
          ${paymentsHTML}
          ${lastSale.change > 0 ? `<div class="row"><span><strong>Cambio:</strong></span><span>$${lastSale.change.toFixed(2)}</span></div>` : ''}
        </div>
        
        <div class="qr">
          <p>Escanea para facturar:</p>
          <img src="${ticketQR}" alt="QR Facturación" />
        </div>
        
        <div class="footer">
          <p>¡Gracias por su compra!</p>
          <p>Conserve este ticket para cualquier aclaración</p>
        </div>
      </body>
      </html>
    `;
  };

  // Cash close calculation
  const calculateCashClose = () => {
    const todaySales = salesHistory;
    
    const data = {
      totalSales: todaySales.length,
      totalAmount: todaySales.reduce((sum, s) => sum + s.total, 0),
      byPaymentMethod: {
        cash: todaySales.filter(s => s.payment_method === 'cash').reduce((sum, s) => sum + s.total, 0),
        debit: todaySales.filter(s => s.payment_method === 'debit').reduce((sum, s) => sum + s.total, 0),
        credit: todaySales.filter(s => s.payment_method === 'credit').reduce((sum, s) => sum + s.total, 0),
        transfer: todaySales.filter(s => s.payment_method === 'transfer').reduce((sum, s) => sum + s.total, 0),
        card: todaySales.filter(s => s.payment_method === 'card').reduce((sum, s) => sum + s.total, 0)
      },
      salesCount: {
        cash: todaySales.filter(s => s.payment_method === 'cash').length,
        debit: todaySales.filter(s => s.payment_method === 'debit').length,
        credit: todaySales.filter(s => s.payment_method === 'credit').length,
        transfer: todaySales.filter(s => s.payment_method === 'transfer').length,
        card: todaySales.filter(s => s.payment_method === 'card').length
      }
    };
    
    setCashCloseData(data);
  };

  const syncData = async () => {
    try {
      await loadProducts();
      await loadSalesHistory();
      toast.success('Datos sincronizados');
    } catch {
      toast.error('Error al sincronizar');
    }
  };

  // Filter products
  const filteredProducts = products.filter(p => 
    p.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.product_sku?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.barcode?.includes(searchQuery)
  );

  const { subtotal, tax, total } = calculateTotals();

  // Show loading
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Cargando POS...</p>
        </div>
      </div>
    );
  }

  // Show login if no session
  if (!session) {
    return <POSLoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="h-[100dvh] bg-slate-950 text-white flex overflow-hidden">
      {/* Desktop Sidebar - hidden on mobile */}
      <aside className="hidden md:flex w-20 bg-slate-900 border-r border-slate-800 flex-col items-center py-4 gap-2">
        <div className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center mb-4">
          <Store className="w-6 h-6 text-white" />
        </div>
        
        <NavButton icon={ShoppingCart} active={activeView === 'sale'} onClick={() => setActiveView('sale')} label="Venta" />
        
        {/* Online Orders Button with Badge */}
        <div className="relative">
          <NavButton 
            icon={ShoppingBag} 
            active={activeView === 'orders'} 
            onClick={handleOrdersViewClick} 
            label="Pedidos" 
          />
          {onlineOrdersCount > 0 && (
            <span className={`absolute -top-1 -right-1 min-w-[20px] h-5 flex items-center justify-center px-1 text-xs font-bold text-white bg-red-500 rounded-full ${isPlayingAlert ? 'animate-pulse' : ''}`}>
              {onlineOrdersCount > 99 ? '99+' : onlineOrdersCount}
            </span>
          )}
        </div>
        
        <NavButton icon={Package} active={activeView === 'inventory'} onClick={() => setActiveView('inventory')} label="Inventario" />
        <NavButton icon={History} active={activeView === 'history'} onClick={() => setActiveView('history')} label="Historial" />
        <NavButton icon={Calculator} active={activeView === 'cashclose'} onClick={() => { setActiveView('cashclose'); calculateCashClose(); }} label="Corte" />
        <NavButton icon={Settings} active={activeView === 'settings'} onClick={() => setActiveView('settings')} label="Config" />
        
        <div className="mt-auto flex flex-col items-center gap-2">
          <button onClick={syncData} className="w-12 h-12 rounded-xl bg-slate-800 hover:bg-slate-700 flex items-center justify-center transition-colors" title="Sincronizar">
            <RefreshCw className="w-5 h-5 text-slate-400" />
          </button>
          
          {/* Offline Indicator */}
          <OfflineIndicator 
            onSyncComplete={(status) => {
              if (status.synced > 0) {
                loadProducts();
                loadSalesHistory();
                toast.success(`${status.synced} venta(s) sincronizada(s)`);
              }
            }} 
          />
          
          <button onClick={handleLogout} className="w-12 h-12 rounded-xl bg-slate-800 hover:bg-red-900/50 flex items-center justify-center transition-colors mt-2" title="Cerrar sesión">
            <LogOut className="w-5 h-5 text-slate-400" />
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 flex pb-16 md:pb-0 min-h-0">
        {activeView === 'sale' && (
          <>
            {/* Products Grid */}
            <div className="flex-1 flex flex-col p-3 md:p-4">
              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Buscar producto o escanear código..."
                    className="w-full pl-12 pr-4 py-3 bg-slate-900 border border-slate-800 rounded-xl text-white placeholder-slate-500 focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    data-testid="pos-search-input"
                  />
                </div>
              </div>

              <div className="flex-1 overflow-y-auto">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-2 md:gap-3">
                  {filteredProducts.map(product => (
                    <ProductCard key={product.product_id} product={product} onAdd={() => addToCart(product)} />
                  ))}
                </div>
                
                {filteredProducts.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-64 text-slate-500">
                    <Package className="w-16 h-16 mb-4 opacity-50" />
                    <p>No se encontraron productos</p>
                  </div>
                )}
              </div>
            </div>

            {/* Cart Panel - desktop only */}
            <div className="hidden md:flex w-96 bg-slate-900 border-l border-slate-800 flex-col">
              <div className="p-4 border-b border-slate-800">
                <div className="flex items-center justify-between">
                  <h2 className="text-lg font-bold text-white">Carrito</h2>
                  {cart.length > 0 && <button onClick={clearCart} className="text-sm text-red-400 hover:text-red-300">Limpiar</button>}
                </div>
                <p className="text-sm text-slate-500">{cart.length} productos</p>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {cart.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-full text-slate-500">
                    <ShoppingCart className="w-12 h-12 mb-2 opacity-50" />
                    <p>Carrito vacío</p>
                    <p className="text-sm">Agrega productos para comenzar</p>
                  </div>
                ) : (
                  cart.map(item => (
                    <CartItem key={item.product_id} item={item} onUpdateQuantity={(d) => updateQuantity(item.product_id, d)} onRemove={() => removeFromCart(item.product_id)} />
                  ))
                )}
              </div>

              <div className="p-4 border-t border-slate-800 space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-slate-400"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
                  <div className="flex justify-between text-slate-400"><span>IVA (16%)</span><span>${tax.toFixed(2)}</span></div>
                  <div className="flex justify-between text-xl font-bold text-white"><span>Total</span><span className="text-teal-400">${total.toFixed(2)}</span></div>
                </div>

                <button
                  onClick={() => setShowPaymentModal(true)}
                  disabled={cart.length === 0}
                  className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl hover:from-teal-600 hover:to-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                  data-testid="pos-checkout-btn"
                >
                  <CreditCard className="w-5 h-5" />
                  Cobrar
                </button>
              </div>
            </div>
          </>
        )}

        {activeView === 'inventory' && <InventoryView products={products} branchName={session.branch.name} />}
        {activeView === 'history' && <HistoryView sales={salesHistory} onReprint={(sale) => { setLastSale(sale); setShowTicketModal(true); }} />}
        {activeView === 'cashclose' && <CashCloseView data={cashCloseData} branchName={session.branch.name} terminalName={session.terminal.name} cashierName={session.cashier.name} />}
        {activeView === 'settings' && <SettingsView session={session} lastSync={lastSync} />}
        {activeView === 'orders' && (
          <OnlineOrdersView
            session={session}
            orderCount={onlineOrdersCount}
            onOrderCountChange={setOnlineOrdersCount}
          />
        )}
      </main>

      {/* Mobile Cart FAB */}
      {activeView === 'sale' && cart.length > 0 && !showMobileCart && (
        <button
          onClick={() => setShowMobileCart(true)}
          className="md:hidden fixed bottom-20 right-4 z-40 bg-teal-500 hover:bg-teal-600 active:bg-teal-700 text-white rounded-full shadow-lg shadow-teal-500/30 flex items-center gap-2 px-5 py-3.5 transition-colors"
        >
          <ShoppingCart className="w-5 h-5" />
          <span className="font-bold">{cart.length}</span>
          <span className="text-sm font-medium">· ${total.toFixed(2)}</span>
        </button>
      )}

      {/* Mobile Cart Overlay */}
      {showMobileCart && activeView === 'sale' && (
        <div className="md:hidden fixed inset-0 z-50 flex flex-col bg-slate-950">
          <div className="flex items-center justify-between p-4 border-b border-slate-800 bg-slate-900">
            <div>
              <h2 className="text-lg font-bold text-white">Carrito</h2>
              <p className="text-sm text-slate-500">{cart.length} productos</p>
            </div>
            <div className="flex items-center gap-3">
              {cart.length > 0 && (
                <button onClick={clearCart} className="text-sm text-red-400 hover:text-red-300 py-1 px-2">Limpiar</button>
              )}
              <button onClick={() => setShowMobileCart(false)} className="p-2 text-slate-400 hover:text-white rounded-lg bg-slate-800">
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {cart.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-slate-500">
                <ShoppingCart className="w-12 h-12 mb-2 opacity-50" />
                <p>Carrito vacío</p>
                <p className="text-sm">Agrega productos para comenzar</p>
              </div>
            ) : (
              cart.map(item => (
                <CartItem key={item.product_id} item={item} onUpdateQuantity={(d) => updateQuantity(item.product_id, d)} onRemove={() => removeFromCart(item.product_id)} />
              ))
            )}
          </div>
          <div className="p-4 border-t border-slate-800 bg-slate-900 space-y-3 pb-6">
            <div className="space-y-1">
              <div className="flex justify-between text-slate-400 text-sm"><span>Subtotal</span><span>${subtotal.toFixed(2)}</span></div>
              <div className="flex justify-between text-slate-400 text-sm"><span>IVA (16%)</span><span>${tax.toFixed(2)}</span></div>
              <div className="flex justify-between text-lg font-bold text-white"><span>Total</span><span className="text-teal-400">${total.toFixed(2)}</span></div>
            </div>
            <button
              onClick={() => { setShowMobileCart(false); setShowPaymentModal(true); }}
              disabled={cart.length === 0}
              className="w-full py-4 bg-gradient-to-r from-teal-500 to-emerald-600 text-white font-bold rounded-xl disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              <CreditCard className="w-5 h-5" />
              Cobrar ${total.toFixed(2)}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="md:hidden fixed bottom-0 inset-x-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-800 flex items-center justify-around px-1 pb-[env(safe-area-inset-bottom)] z-40">
        <MobileNavButton icon={ShoppingCart} active={activeView === 'sale'} onClick={() => setActiveView('sale')} label="Venta" />
        <div className="relative">
          <MobileNavButton icon={ShoppingBag} active={activeView === 'orders'} onClick={handleOrdersViewClick} label="Pedidos" />
          {onlineOrdersCount > 0 && (
            <span className={`absolute -top-1 right-0 min-w-[18px] h-[18px] flex items-center justify-center px-1 text-[10px] font-bold text-white bg-red-500 rounded-full ${isPlayingAlert ? 'animate-pulse' : ''}`}>
              {onlineOrdersCount > 99 ? '99+' : onlineOrdersCount}
            </span>
          )}
        </div>
        <MobileNavButton icon={Package} active={activeView === 'inventory'} onClick={() => setActiveView('inventory')} label="Stock" />
        <MobileNavButton icon={History} active={activeView === 'history'} onClick={() => setActiveView('history')} label="Ventas" />
        <MobileNavButton icon={Calculator} active={activeView === 'cashclose'} onClick={() => { setActiveView('cashclose'); calculateCashClose(); }} label="Corte" />
        <MobileNavButton icon={LogOut} active={false} onClick={handleLogout} label="Salir" />
      </nav>

      {/* Payment Modal */}
      {showPaymentModal && (
        <MixedPaymentModal total={total} onPayment={processPayment} onClose={() => setShowPaymentModal(false)} />
      )}

      {/* Ticket Modal */}
      {showTicketModal && lastSale && (
        <TicketModal sale={lastSale} session={session} qrCode={ticketQR} onPrint={printTicket} onClose={() => setShowTicketModal(false)} />
      )}
    </div>
  );
};

// ==================== SUB COMPONENTS ====================

const NavButton = ({ icon: Icon, active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`w-14 h-14 rounded-xl flex flex-col items-center justify-center gap-1 transition-all ${
      active ? 'bg-teal-500/20 text-teal-400 border border-teal-500/50' : 'text-slate-500 hover:bg-slate-800 hover:text-slate-300'
    }`}
    title={label}
  >
    <Icon className="w-5 h-5" />
    <span className="text-[10px]">{label}</span>
  </button>
);

const MobileNavButton = ({ icon: Icon, active, onClick, label }) => (
  <button
    onClick={onClick}
    className={`flex flex-col items-center justify-center py-2 px-2.5 rounded-lg transition-all ${
      active ? 'text-teal-400' : 'text-slate-500 active:text-slate-300'
    }`}
  >
    <Icon className="w-5 h-5" />
    <span className="text-[10px] mt-0.5">{label}</span>
  </button>
);

const ProductCard = ({ product, onAdd }) => (
  <button onClick={onAdd} className="bg-slate-900 border border-slate-800 rounded-xl p-3 text-left hover:border-teal-500/50 hover:bg-slate-800/50 transition-all group" data-testid={`pos-product-${product.product_id}`}>
    <div className="aspect-square bg-slate-800 rounded-lg mb-2 flex items-center justify-center overflow-hidden">
      {product.image_url ? <img src={product.image_url} alt={product.product_name} className="w-full h-full object-cover" /> : <Package className="w-8 h-8 text-slate-600" />}
    </div>
    <h3 className="font-medium text-white text-sm truncate">{product.product_name}</h3>
    <p className="text-xs text-slate-500 truncate">{product.product_sku}</p>
    <div className="flex items-center justify-between mt-2">
      <span className="text-teal-400 font-bold">${(product.retail_price || 0).toFixed(2)}</span>
      <span className="text-xs text-slate-500">{product.quantity} disp.</span>
    </div>
    <div className="mt-1.5 md:mt-2 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
      <div className="w-full py-1.5 md:py-1 bg-teal-500/20 text-teal-400 text-xs font-medium rounded text-center">+ Agregar</div>
    </div>
  </button>
);

const CartItem = ({ item, onUpdateQuantity, onRemove }) => (
  <div className="bg-slate-800 rounded-xl p-3" data-testid={`cart-item-${item.product_id}`}>
    <div className="flex items-start justify-between mb-2">
      <div className="flex-1 min-w-0">
        <h4 className="font-medium text-white text-sm truncate">{item.product_name}</h4>
        <p className="text-xs text-slate-500">${item.unit_price.toFixed(2)} c/u</p>
      </div>
      <button onClick={onRemove} className="text-slate-500 hover:text-red-400 ml-2"><Trash2 className="w-4 h-4" /></button>
    </div>
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <button onClick={() => onUpdateQuantity(-1)} className="w-8 h-8 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center justify-center"><Minus className="w-4 h-4" /></button>
        <span className="w-8 text-center font-bold">{item.quantity}</span>
        <button onClick={() => onUpdateQuantity(1)} className="w-8 h-8 bg-slate-700 hover:bg-slate-600 rounded-lg flex items-center justify-center"><Plus className="w-4 h-4" /></button>
      </div>
      <span className="font-bold text-teal-400">${(item.unit_price * item.quantity).toFixed(2)}</span>
    </div>
  </div>
);

const TicketModal = ({ sale, session, qrCode, onPrint, onClose }) => (
  <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl max-w-sm w-full p-6 text-slate-900">
      <div className="text-center mb-4">
        <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <Check className="w-8 h-8 text-emerald-600" />
        </div>
        <h2 className="text-xl font-bold">¡Venta Exitosa!</h2>
        <p className="text-slate-500">Ticket #{sale.ticket_number}</p>
      </div>
      
      <div className="bg-slate-100 rounded-xl p-4 mb-4">
        <div className="flex justify-between mb-2"><span className="text-slate-600">Total:</span><span className="font-bold text-xl">${sale.total.toFixed(2)}</span></div>
        {sale.change > 0 && <div className="flex justify-between text-emerald-600 font-medium"><span>Cambio:</span><span>${sale.change.toFixed(2)}</span></div>}
      </div>
      
      <div className="text-center mb-4">
        <p className="text-sm text-slate-500 mb-2">QR para facturar:</p>
        {qrCode && <img src={qrCode} alt="QR Facturación" className="mx-auto w-32 h-32" />}
      </div>
      
      <div className="flex gap-3">
        <button onClick={onPrint} className="flex-1 py-3 bg-slate-900 hover:bg-slate-800 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors">
          <Printer className="w-5 h-5" />Imprimir
        </button>
        <button onClick={onClose} className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white font-medium rounded-xl transition-colors">Nueva Venta</button>
      </div>
    </div>
  </div>
);

const InventoryView = ({ products, branchName }) => (
  <div className="flex-1 p-3 md:p-6 overflow-y-auto">
    <h2 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">Inventario</h2>
    <p className="text-slate-400 text-sm md:text-base mb-3 md:mb-6">Sucursal: {branchName}</p>
    {/* Mobile card layout */}
    <div className="md:hidden space-y-2">
      {products.map(product => (
        <div key={product.product_id} className="bg-slate-900 border border-slate-800 rounded-xl p-3 flex items-center justify-between">
          <div className="min-w-0 flex-1">
            <h4 className="font-medium text-white text-sm truncate">{product.product_name}</h4>
            <p className="text-xs text-slate-500 font-mono">{product.product_sku}</p>
          </div>
          <div className="flex items-center gap-4 ml-3">
            <span className={`text-sm font-bold ${product.quantity <= (product.stock_min || 5) ? 'text-red-400' : 'text-emerald-400'}`}>{product.quantity}</span>
            <span className="text-teal-400 font-bold text-sm">${(product.retail_price || 0).toFixed(2)}</span>
          </div>
        </div>
      ))}
    </div>
    {/* Desktop table */}
    <div className="hidden md:block bg-slate-900 rounded-xl border border-slate-800 overflow-hidden">
      <table className="w-full">
        <thead className="bg-slate-800">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">Producto</th>
            <th className="px-4 py-3 text-left text-xs font-medium text-slate-400 uppercase">SKU</th>
            <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Stock</th>
            <th className="px-4 py-3 text-center text-xs font-medium text-slate-400 uppercase">Min.</th>
            <th className="px-4 py-3 text-right text-xs font-medium text-slate-400 uppercase">Precio</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-800">
          {products.map(product => (
            <tr key={product.product_id} className="hover:bg-slate-800/50">
              <td className="px-4 py-3 text-white">{product.product_name}</td>
              <td className="px-4 py-3 text-slate-400 font-mono text-sm">{product.product_sku}</td>
              <td className="px-4 py-3 text-center">
                <span className={`font-bold ${product.quantity <= (product.stock_min || 5) ? 'text-red-400' : 'text-emerald-400'}`}>{product.quantity}</span>
              </td>
              <td className="px-4 py-3 text-center text-slate-500">{product.stock_min || 5}</td>
              <td className="px-4 py-3 text-right text-teal-400 font-medium">${(product.retail_price || 0).toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

const HistoryView = ({ sales, onReprint }) => (
  <div className="flex-1 p-3 md:p-6 overflow-y-auto">
    <h2 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">Historial del Día</h2>
    <p className="text-slate-400 text-sm md:text-base mb-3 md:mb-6">{sales.length} ventas registradas</p>
    <div className="space-y-2 md:space-y-3">
      {sales.map(sale => (
        <div key={sale.id} className="bg-slate-900 border border-slate-800 rounded-xl p-3 md:p-4 flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p className="font-bold text-white text-sm md:text-base truncate">{sale.ticket_number}</p>
            <p className="text-xs md:text-sm text-slate-400">{new Date(sale.created_at).toLocaleTimeString('es-MX')}</p>
            <p className="text-xs md:text-sm text-slate-500">{sale.items?.length || 0} productos</p>
          </div>
          <div className="text-right shrink-0">
            <p className="text-base md:text-xl font-bold text-teal-400">${sale.total.toFixed(2)}</p>
            <PaymentMethodBadge method={sale.payment_method} />
          </div>
          <button onClick={() => onReprint(sale)} className="shrink-0 ml-1 md:ml-4 p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors">
            <Printer className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
      ))}
      {sales.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay ventas registradas hoy</p>
        </div>
      )}
    </div>
  </div>
);

const PaymentMethodBadge = ({ method }) => {
  const styles = {
    cash: 'bg-emerald-900/50 text-emerald-400',
    debit: 'bg-blue-900/50 text-blue-400',
    credit: 'bg-purple-900/50 text-purple-400',
    transfer: 'bg-orange-900/50 text-orange-400',
    card: 'bg-blue-900/50 text-blue-400'
  };
  const labels = { cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', transfer: 'Transferencia', card: 'Tarjeta' };
  return <span className={`text-xs px-2 py-1 rounded-full ${styles[method] || 'bg-slate-700 text-slate-400'}`}>{labels[method] || method}</span>;
};

const CashCloseView = ({ data, branchName, terminalName, cashierName }) => {
  const printCashClose = () => {
    if (!data) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Corte de Caja</title>
        <style>
          body { font-family: 'Courier New', monospace; font-size: 12px; width: 280px; margin: 0 auto; padding: 20px; }
          h2 { text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; }
          .row { display: flex; justify-content: space-between; margin: 8px 0; }
          .total { font-size: 16px; font-weight: bold; border-top: 2px solid #000; padding-top: 10px; margin-top: 10px; }
          .section { margin: 15px 0; padding: 10px 0; border-top: 1px dashed #000; }
        </style>
      </head>
      <body>
        <h2>CORTE DE CAJA</h2>
        <div class="row"><span>Fecha:</span><span>${new Date().toLocaleDateString('es-MX')}</span></div>
        <div class="row"><span>Hora:</span><span>${new Date().toLocaleTimeString('es-MX')}</span></div>
        <div class="row"><span>Sucursal:</span><span>${branchName || ''}</span></div>
        <div class="row"><span>Terminal:</span><span>${terminalName || ''}</span></div>
        <div class="row"><span>Cajero:</span><span>${cashierName || ''}</span></div>
        <div class="section"><div class="row"><span>Total Ventas:</span><span>${data.totalSales}</span></div></div>
        <div class="section">
          <strong>DESGLOSE POR MÉTODO:</strong>
          <div class="row"><span>Efectivo (${data.salesCount.cash}):</span><span>$${data.byPaymentMethod.cash.toFixed(2)}</span></div>
          <div class="row"><span>Débito (${data.salesCount.debit}):</span><span>$${data.byPaymentMethod.debit.toFixed(2)}</span></div>
          <div class="row"><span>Crédito (${data.salesCount.credit}):</span><span>$${data.byPaymentMethod.credit.toFixed(2)}</span></div>
          <div class="row"><span>Transferencia (${data.salesCount.transfer}):</span><span>$${data.byPaymentMethod.transfer.toFixed(2)}</span></div>
        </div>
        <div class="row total"><span>TOTAL:</span><span>$${data.totalAmount.toFixed(2)}</span></div>
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  if (!data) return <div className="flex-1 flex items-center justify-center text-slate-500"><p>Calculando corte de caja...</p></div>;

  return (
    <div className="flex-1 p-3 md:p-6 overflow-y-auto">
      <h2 className="text-xl md:text-2xl font-bold text-white mb-1 md:mb-2">Corte de Caja</h2>
      <p className="text-slate-400 mb-6">{new Date().toLocaleDateString('es-MX', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
      <div className="max-w-xl space-y-6">
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
          <div className="text-center mb-6">
            <p className="text-slate-400 text-sm">Total del Día</p>
            <p className="text-4xl font-bold text-teal-400">${data.totalAmount.toFixed(2)}</p>
            <p className="text-slate-500">{data.totalSales} ventas</p>
          </div>
          <div className="space-y-3">
            <PaymentMethodRow icon={Banknote} label="Efectivo" count={data.salesCount.cash} amount={data.byPaymentMethod.cash} color="emerald" />
            <PaymentMethodRow icon={CreditCard} label="Débito" count={data.salesCount.debit} amount={data.byPaymentMethod.debit} color="blue" />
            <PaymentMethodRow icon={CreditCard} label="Crédito" count={data.salesCount.credit} amount={data.byPaymentMethod.credit} color="purple" />
            <PaymentMethodRow icon={Receipt} label="Transferencia" count={data.salesCount.transfer} amount={data.byPaymentMethod.transfer} color="orange" />
          </div>
        </div>
        <button onClick={printCashClose} className="w-full py-4 bg-slate-800 hover:bg-slate-700 text-white font-medium rounded-xl flex items-center justify-center gap-2 transition-colors">
          <Printer className="w-5 h-5" />Imprimir Corte
        </button>
      </div>
    </div>
  );
};

const PaymentMethodRow = ({ icon: Icon, label, count, amount, color }) => {
  const colorClasses = {
    emerald: 'bg-emerald-900/20 border-emerald-900/50 text-emerald-400',
    blue: 'bg-blue-900/20 border-blue-900/50 text-blue-400',
    purple: 'bg-purple-900/20 border-purple-900/50 text-purple-400',
    orange: 'bg-orange-900/20 border-orange-900/50 text-orange-400'
  };
  return (
    <div className={`flex items-center justify-between p-3 rounded-lg border ${colorClasses[color]}`}>
      <div className="flex items-center gap-3">
        <Icon className="w-6 h-6" />
        <div><p className="text-white font-medium">{label}</p><p className={`text-sm`}>{count} ventas</p></div>
      </div>
      <p className="text-xl font-bold">${amount.toFixed(2)}</p>
    </div>
  );
};

const SettingsView = ({ session, lastSync }) => (
  <div className="flex-1 p-3 md:p-6 overflow-y-auto">
    <h2 className="text-xl md:text-2xl font-bold text-white mb-4 md:mb-6">Configuración</h2>
    <div className="max-w-xl space-y-6">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Building2 className="w-5 h-5 text-teal-400" />Negocio</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Nombre:</span><span className="text-white">{session.business?.name || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">RFC:</span><span className="text-white font-mono">{session.business?.rfc || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Teléfono:</span><span className="text-white">{session.business?.phone || 'N/A'}</span></div>
        </div>
      </div>
      
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><Store className="w-5 h-5 text-teal-400" />Terminal POS</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Sucursal:</span><span className="text-white">{session.branch?.name || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Terminal:</span><span className="text-white">{session.terminal?.name || 'N/A'}</span></div>
          <div className="flex justify-between"><span className="text-slate-400">Código:</span><span className="text-white font-mono text-xs">{session.terminal?.hash || 'N/A'}</span></div>
        </div>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><User className="w-5 h-5 text-teal-400" />Cajero</h3>
        <div className="space-y-3 text-sm">
          <div className="flex justify-between"><span className="text-slate-400">Nombre:</span><span className="text-white">{session.cashier?.name || 'N/A'}</span></div>
        </div>
      </div>
      
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-6">
        <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2"><RefreshCw className="w-5 h-5 text-teal-400" />Sincronización</h3>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-slate-400 text-sm">Última sincronización:</p>
            <p className="text-white font-medium">{lastSync ? lastSync.toLocaleString('es-MX') : 'Nunca'}</p>
          </div>
          <div className="flex items-center gap-2 text-emerald-400">
            <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
            <span className="text-sm">En línea</span>
          </div>
        </div>
      </div>
    </div>
  </div>
);

export default POSMain;
